import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectsSummary } from '../hooks/useProjectsSummary';
import { useGlobal } from '../../../common/contexts/GlobalContext';
import { fetchWithAuth } from '../../../common/utils/apiUtils';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { deleteProject, fetchStatuses, fetchCertifiers } from '../services/projectService';

const ProjectList = () => {
  const navigate = useNavigate();
  const { } = useGlobal(); // Preparado para futuros servicios
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [certifiers, setCertifiers] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const { content: projects, totalElements, loading, error } = useProjectsSummary(filters, page, size);
  const [inputValues, setInputValues] = useState({ name: '', departamento: '' });
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalLoaded, setTotalLoaded] = useState(false);
  
  // Obtener el total de proyectos sin filtros (solo una vez)
  useEffect(() => {
    const fetchTotal = async () => {
      if (totalLoaded) return;
      try {
        const response = await fetchWithAuth(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api'}/projects/list?page=0&size=1`);
        const data = await response.json();
        setTotalProjects(data.totalElements || 0);
        setTotalLoaded(true);
      } catch (err) {
        console.error('Error fetching total projects:', err);
      }
    };
    fetchTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Determinar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    return Object.keys(filters).some(key => filters[key] && filters[key] !== '');
  }, [filters]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [statusesData, certifiersData] = await Promise.all([
          fetchStatuses(),
          fetchCertifiers()
        ]);
        setStatuses(statusesData);
        setCertifiers(certifiersData);
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };
    loadOptions();
  }, []);


  useEffect(() => {
    setInputValues({
      name: filters.name || '',
      departamento: filters.departamento || ''
    });
  }, [filters.name, filters.departamento]);

  const confirmDelete = (project) => {
    setProjectToDelete(project);
    setDeleteDialogVisible(true);
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteProject(projectToDelete.id);
      setDeleteDialogVisible(false);
      setProjectToDelete(null);
      // Reload the page to refresh the list
      window.location.reload();
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Error al eliminar el proyecto');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-content-center p-4">
        <ProgressSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Message
        severity="error"
        text={`Error al cargar proyectos: ${error}`}
        className="w-full"
      />
    );
  }

  const getStatusSeverity = (status) => {
    switch (status?.name) {
      case 'CERTIFICADO':
        return 'success';
      case 'EN_DESARROLLO':
        return 'info';
      case 'REGISTRO_COMPLETO':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const statusTemplate = (rowData) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Badge
          value={rowData.status?.name}
          severity={getStatusSeverity(rowData.status)}
          style={{ 
            minWidth: '80px', 
            textAlign: 'center', 
            display: 'inline-block',
            whiteSpace: 'normal',
            height: 'auto',
            padding: '0.4em 0.8em',
            lineHeight: '1.2'
          }}
        />
      </div>
    );
  };

  const hectaresTemplate = (rowData) => {
    return `${rowData.hectares?.toLocaleString()} ha`;
  };

  const polygonTemplate = (rowData) => {
    return rowData.hasGeometry ? (
      <i className="pi pi-check-circle" style={{ fontSize: '1.2rem', color: '#16a34a' }}></i>
    ) : (
      <i className="pi pi-times-circle" style={{ fontSize: '1.2rem', color: '#f97316' }}></i>
    );
  };

  const actionsTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-info"
          tooltip="Ver detalles"
          onClick={() => navigate(`/admin/projects/${rowData.id}`)}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-warning"
          tooltip="Editar"
          onClick={() => navigate(`/admin/projects/${rowData.id}/edit`)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          tooltip="Eliminar"
          onClick={() => confirmDelete(rowData)}
        />
      </div>
    );
  };

  const handleInputChange = (key, value) => {
    setInputValues(prev => ({ ...prev, [key]: value }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleKeyDown = (key, e) => {
    if (e.key === 'Enter') {
      handleFilterChange(key, inputValues[key]);
    }
  };

  const clearFilters = () => {
    setFilters({});
    setPage(0);
  };

  const onPage = (event) => {
    setPage(event.page);
    setSize(event.rows);
  };

  return (
    <div>
      <div className="p-fluid grid mb-4 align-items-end">
        <div className="col-12 md:col-3">
          <label htmlFor="nameFilter" className="block text-900 font-medium mb-2">Nombre</label>
          <InputText
            id="nameFilter"
            value={inputValues.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            onBlur={(e) => handleFilterChange('name', inputValues.name)}
            onKeyDown={(e) => handleKeyDown('name', e)}
            placeholder="Buscar por nombre"
          />
        </div>
        <div className="col-12 md:col-3">
          <label htmlFor="departamentoFilter" className="block text-900 font-medium mb-2">Departamento</label>
          <InputText
            id="departamentoFilter"
            value={inputValues.departamento}
            onChange={(e) => handleInputChange('departamento', e.target.value)}
            onBlur={(e) => handleFilterChange('departamento', inputValues.departamento)}
            onKeyDown={(e) => handleKeyDown('departamento', e)}
            placeholder="Buscar por departamento"
          />
        </div>
        <div className="col-12 md:col-2">
          <label htmlFor="estadoFilter" className="block text-900 font-medium mb-2">Estado</label>
          <Dropdown
            id="estadoFilter"
            value={filters.estado || ''}
            options={statuses.map(s => ({ label: s.name, value: s.name }))}
            onChange={(e) => handleFilterChange('estado', e.value)}
            placeholder="Seleccionar estado"
            showClear
          />
        </div>
        <div className="col-12 md:col-2">
          <label htmlFor="certifierFilter" className="block text-900 font-medium mb-2">Certificador</label>
          <Dropdown
            id="certifierFilter"
            value={filters.certifierId || ''}
            options={certifiers.map(c => ({ label: c.name, value: c.id }))}
            onChange={(e) => handleFilterChange('certifierId', e.value)}
            placeholder="Seleccionar certificador"
            showClear
          />
        </div>
        <div className="col-12 md:col-2">
          <div className="flex align-items-end gap-2">
            <Button
              icon="pi pi-filter-slash"
              className="p-button-secondary p-button-sm p-button-rounded"
              tooltip="Limpiar filtros"
              onClick={clearFilters}
              style={{ width: '36px', height: '36px' }}
            />
            <div className="p-2 surface-100 border-round" style={{ minWidth: '140px' }}>
              <span className="text-sm font-medium">
                {hasActiveFilters ? (
                  <>
                    <i className="pi pi-filter text-primary mr-1"></i>
                    Proyectos filtrados: <strong>{totalElements}</strong>
                  </>
                ) : (
                  <>
                    <i className="pi pi-list text-primary mr-1"></i>
                    Total proyectos: <strong>{totalProjects}</strong>
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {projects.length === 0 ? (
        <Message
          severity="info"
          text="No projects found."
          className="w-full"
        />
      ) : (
        <DataTable
          value={projects}
          lazy
          paginator
          first={page * size}
          rows={size}
          totalRecords={totalElements}
          rowsPerPageOptions={[5, 10, 25]}
          onPage={onPage}
          responsiveLayout="scroll"
          emptyMessage="No projects found."
        >
          <Column field="name" header="Nombre" style={{ minWidth: '200px' }} />
          <Column field="location" header="Ubicación" style={{ minWidth: '150px' }} />
          <Column field="departamento" header="Departamento" style={{ minWidth: '120px' }} />
          <Column field="description" header="Descripción" style={{ minWidth: '200px' }} />
          <Column field="status" header="Estado" body={statusTemplate} style={{ minWidth: '180px' }} />
          <Column field="hectares" header="Hectáreas" body={hectaresTemplate} style={{ minWidth: '100px' }} />
          <Column header="Polígono" body={polygonTemplate} style={{ minWidth: '60px' }} />
          <Column header="Acciones" body={actionsTemplate} style={{ minWidth: '150px' }} />
        </DataTable>
      )}

      <ConfirmDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        message={`¿Estás seguro de que quieres eliminar el proyecto "${projectToDelete?.name}"?`}
        header="Confirmar Eliminación"
        icon="pi pi-exclamation-triangle"
        accept={handleDelete}
        reject={() => setDeleteDialogVisible(false)}
        acceptLabel="Eliminar"
        rejectLabel="Cancelar"
        acceptClassName="p-button-danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default ProjectList;